/* 地址公共方法 */

// 三方库/框架
import to from "await-to-js";
// 公共方法
import cookieUtils from "@/utils/cookieUtils";
import { parseCookieString } from "@/utils/cookieUtils";
import { queryGlobalCountriesData } from "@/utils/countryUtils";
import { getJDISiteKeys } from "@/utils/multiSite";
import { DEFAULT_COUNTRY_ADDRESSES } from "@/constants/address";
// 接口
import { 
  Address, 
  LocationPermission, 
  GeolocationLocation, 
  GeolocationOptions,
  GeolocationApiRequest,
  GeolocationApiResponse
} from "@/apis/address/address.interface";
import { getLocationDetail } from "@/apis/address/address.api";


// Default address cookie name
export const ADDRESS_KEY = "USER_ADDRESS_AREA_KEY";
// Default cascade (postCode info) address cookie name
export const AREA = "CASCADE_ADDRESS_AREA_KEY";
// Default cookie timeout: Unit: days
export const EXPIRE_DAYS = 30;

/**
 * 获取地址数据
 * @returns 返回地址数据，如果没有则返回null
 */
export const getAddressCookie = (): Address | null => {
  // 优先从 ADDRESS_KEY 获取数据，如果没有再从 AREA 获取
  const addressStr: string = cookieUtils.getCookie(ADDRESS_KEY) || "";
  if (addressStr) {
    try {
      const address = JSON.parse(decodeURI(addressStr));
      if (address) {
        return address;
      }
    } catch (e) {
      console.log(e);
    }
  }
  
  const areaStr: string = cookieUtils.getCookie(AREA) || "";
  if (areaStr) {
    try {
      const area = JSON.parse(decodeURI(areaStr));
      if (area) {
        return area;
      }
    } catch (e) {
      console.log(e);
    }
  }
  
  return null;
};

/**
 * 设置地址数据
 * @param data 需要设置的地址数据
 */
export const setAddressCookie = (data?: Address): void => {
  if (!data) return;
  
  try {
    // 只保留需要的字段，减少cookie大小
    const filteredData: Partial<Address> = {};
    
    // 保留指定字段
    const fieldsToKeep = [
      'areaCode', 'countryCode', 'countryName', 'countryId', 'postCode', 
      'siteCode', 'siteId', 'addressId', 'pickUpSiteId', 'addressDetail', 
      'fullAddress', 'isDeliverable', 'provinceId', 'cityId',
      'districtId', 'countyId', 'townId', 'mobile', 'name', 
      'defAddress', 'displayOverArea', 'longitude', 'latitude',
      'provinceName', 'cityName', 'districtName', 'countyName', 'townName'
    ];
    
    fieldsToKeep.forEach(field => {
      if (data[field as keyof Address] !== undefined) {
        filteredData[field as keyof Address] = data[field as keyof Address];
      }
    });
    
    if (isAddress(data)) {
      // 是完整地址，存储到 ADDRESS_KEY
      const addressStr = encodeURI(JSON.stringify(filteredData));
      cookieUtils.setCookie(ADDRESS_KEY, addressStr, EXPIRE_DAYS);
      // 清除 AREA
      cookieUtils.setCookie(AREA, "", EXPIRE_DAYS);
    } else if (isPostCodeAddress(data)) {
      // 是邮编地址，存储到 AREA
      const areaStr = encodeURI(JSON.stringify(filteredData));
      cookieUtils.setCookie(AREA, areaStr, EXPIRE_DAYS);
      // 清除 ADDRESS_KEY
      cookieUtils.setCookie(ADDRESS_KEY, "", EXPIRE_DAYS);
    }

    localStorage.setItem("countryAlertDismissed", "false");
  } catch (e) {
    console.log(e);
  }
};

/**
 * 清除地址数据
 */
export const clearAddressCookie = (): void => {
  // 清除两个 cookie
  try {
    cookieUtils.setCookie(ADDRESS_KEY, "", EXPIRE_DAYS);
    cookieUtils.setCookie(AREA, "", EXPIRE_DAYS);
  } catch (e) {
    console.log(e);
  }
};

// 初始化缓存中的地址
export const initAddress = (): Promise<Address | null> => {
  return new Promise((resolve) => {
    const addressData = getAddressCookie();
    if (addressData) {
      resolve(addressData as Address);
      return;
    }
    resolve(null);
  });
};

// 四级地址等相关参数
export const areaAddress = async () => {
  const data = await initAddress();
  const body: { [key: string]: any } = {};
  // 获取缓存postCode
  if (data) {
    body["postCode"] = data?.postCode;
    body['area'] = [
      data?.provinceId || 0,
      data?.cityId || 0,
      data?.districtId || 0,
      data?.townId || 0
    ].join("_");
    if(data?.pickUpSiteId) {
      body["pickUpSiteId"] = data.pickUpSiteId;
    }
    if(data?.addressId) {
      body["addressId"] = data.addressId;
    }
    return body;
  }
  const defaultAddressBody = getDefaultAddressBody();
  return defaultAddressBody;
};

// 获取当前缓存的状态
export const curIsLocal = async () => {
  const data = await initAddress();
  // 获取缓存postCode
  if (data?.isDefaultLocal) {
    return false;
  }
  if (data) {
    return true;
  }
  return false;
};

// 四级地址等相关参数(给ssr项目单独使用)
export const getSSrAddressBody = (cookie: string) => {
  // 这里通过传入的cookie，来获取对应的 getAddress 或者 getArea 最后把获取的值生成一个对象 回传回去
  let body: { [key: string]: any } = {};
  const customCookies = parseCookieString(cookie); // 先解析传入的cookie字符串为对象
  const addressStr = customCookies[ADDRESS_KEY];
  const areaStr = customCookies[AREA];
  if (addressStr) {
    try {
      const address = JSON.parse(decodeURIComponent(decodeURIComponent(addressStr)));
      if (address) {
        body["area"] = [
          address?.provinceId || 0,
          address?.cityId || 0,
          address?.districtId || 0,
          address?.townId || 0
        ].join("_");
        body['postCode'] = address?.postCode;

        if(address?.pickUpSiteId) {
          body["pickUpSiteId"] = address.pickUpSiteId;
        }
        if(address?.addressId) {
          body["addressId"] = address.addressId;
        }
        return body;
      }
    } catch (e) {
      console.log(e);
    }
  }
  if (areaStr) {
    try {
      const area = JSON.parse(decodeURIComponent(decodeURIComponent(areaStr)));
      if (area) {
        body["area"] = [
          area?.provinceId || 0,
          area?.cityId || 0,
          area?.districtId || 0,
          area?.townId || 0
        ].join("_");
        body['postCode'] = area?.postCode;

        if(area?.pickUpSiteId) {
          body["pickUpSiteId"] = area.pickUpSiteId;
        }
        if(area?.addressId) {
          body["addressId"] = area.addressId;
        }

        return body;
      }
    } catch (e) {
      console.log(e);
    }
  }
  const defaultAddressBody = getDefaultAddressBody();
  return defaultAddressBody;
};

export const isPostCodeAddress = (data: any) => {
  if (data === null) {
    return false;
  }
  return data?.postCode && !data?.addressId;
};

export const isAddress = (data: any) => {
  if (data === null) {
    return false;
  }
  return data?.postCode && data?.addressId || data?.postCode && data?.pickUpSiteId;
};

/**
 * 查询网站定位授权状态
 * @returns 定位权限状态
 */
const checkGeolocationPermission = async (): Promise<LocationPermission> => {
  if (!navigator?.geolocation) {
    return "denied";
  }

  try {
    // 尝试获取位置以触发权限请求
    const permissionStatus = await navigator.permissions?.query({ name: "geolocation" });
    return permissionStatus.state as LocationPermission;
  } catch (error) {
    return "denied";
  }
};

/**
 * 获取网站定位
 * @param options 定位选项
 * @returns 位置信息
 */
export const getGeolocationByWebApi = (options?: GeolocationOptions): Promise<GeolocationLocation | null> => {
  if (!navigator?.geolocation) {
    return Promise.resolve(null);
  }

  const geolocationOptions = {
    enableHighAccuracy: options?.enableHighAccuracy ?? true,
    timeout: options?.timeout ?? 10000,
    maximumAge: options?.maximumAge ?? 0
  };

  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        const newPosition = { lat: latitude, lng: longitude };
        
        resolve(newPosition);
      },
      (error) => {
        resolve(null);
      },
      geolocationOptions
    );
  });
};

/**
 * 通过谷歌地图API获取设备位置（经纬度）
 * 谷歌api: https://developers.google.com/maps/documentation/geolocation/overview
 * @param options homeMobileCountryCode手机代码、homeMobileNetworkCode网络代码、radioType无线电类型、carrier运营商
 * @returns 返回位置信息（经纬度）
 */
export const getGeolocationByGoogleApi = async (options?: {
  homeMobileCountryCode?: number;
  homeMobileNetworkCode?: number;
  radioType?: string;
  carrier?: string;
}): Promise<GeolocationLocation | null> => {
  try {
    const apiKey = process.env.NEXT_PUBLIC_GOOGLE_MAP_API_KEY;
    if (!apiKey) {
      return null;
    }

    const requestBody: GeolocationApiRequest = {
      ...options,
      considerIp: true // 开启IP辅助定位
    };

    const response = await fetch(
      `https://www.googleapis.com/geolocation/v1/geolocate?key=${apiKey}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      }
    );

    if (!response.ok) {
      return null;
    }

    const data: GeolocationApiResponse = await response.json();
    
    if (data.location && data.location.lat && data.location.lng) {
      return {
        lat: data.location.lat,
        lng: data.location.lng
      };
    }
    
    return null;
  } catch (error) {
    console.error('谷歌查询位置失败:', error);
    return null;
  }
};

/**
 * 获取设备位置信息（经纬度）
 * 优先使用网站定位权限 如果没有权限则尝试通过Google API获取
 * @param options 定位信息查询配置项 包含webOptions和googleOptions
 * @returns 返回位置信息（经纬度）
 */
export const getGeolocation = async (
  options?: {
    webOptions?: GeolocationOptions;
    googleOptions?: {
      homeMobileCountryCode?: number;
      homeMobileNetworkCode?: number;
      radioType?: string;
      carrier?: string;
    };
  }
): Promise<GeolocationLocation | null> => {
  try {
    let geolocationPosition = null;
    const locationPermission = await checkGeolocationPermission();
    if (locationPermission === "granted") {
      // 网站有定位授权 优先使用网站定位
      geolocationPosition = await getGeolocationByWebApi(options?.webOptions);
    } else {
      // 网站没有定位授权 尝试通过Google api获取定位（默认ip辅助定位）
      geolocationPosition = await getGeolocationByGoogleApi(options?.googleOptions);
    }
    if (!geolocationPosition) {
      return null;
    }
    return geolocationPosition;
  } catch (error) {
    console.error('获取位置信息失败:', error);
    return null;
  }
};

/**
 * 通过经纬度获取国家代码
 * 谷歌api: https://developers.google.com/maps/documentation/javascript/reference/3.58/geocoder
 * @param location 位置信息（经纬度）
 * @returns 返回国家代码
 */
export const getCountryCodeByGeolocation = async (location: GeolocationLocation): Promise<string | null> => {
  try {
    const apiKey = process.env.NEXT_PUBLIC_GOOGLE_MAP_API_KEY;
    if (!apiKey) {
      return null;
    }
    
    const { lat, lng } = location;
    
    const geocodingResponse = await fetch(
      `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${apiKey}`
    );
    
    if (!geocodingResponse.ok) {
      return null;
    }
    
    const geocodingData = await geocodingResponse.json();
    
    if (geocodingData.status === 'OK' && geocodingData.results && geocodingData.results.length > 0) {
      for (const result of geocodingData.results) {
        if (result.address_components) {
          for (const component of result.address_components) {
            if (component.types && component.types.includes('country')) {
              return component.short_name; // 国家代码
            }
          }
        }
      }
    }
    
    return null;
  } catch (error) {
    console.error('谷歌地图国家代码查询失败:', error);
    return null;
  }
};

/**
 * 通过定位信息获取国家代码
 * @param options 定位选项，包含webOptions和googleOptions
 * @returns 返回国家代码
 */
export const getGeolocationCountryCode = async (options?: {
  webOptions?: GeolocationOptions;
  googleOptions?: {
    homeMobileCountryCode?: number;
    homeMobileNetworkCode?: number;
    radioType?: string;
    carrier?: string;
  };
}): Promise<string | null> => {
  try {
    const geolocationPosition = await getGeolocation(options);
    if (!geolocationPosition) {
      return null;
    }
    
    return await getCountryCodeByGeolocation(geolocationPosition);
  } catch (error) {
    console.error('国家代码查询失败:', error);
    return null;
  }
};

/**
 * 通过定位信息获取地址详情
 * @param param options - 定位选项，包含webOptions和googleOptions; verifyType - 定位校验方案 默认0不校验 1按国家维度校验 2按站点维度校验
 * @returns 返回地址详情字符串
 */
export const getGeolocationAddressInfo = async (
  param?: {
    options?: {
      webOptions?: GeolocationOptions;
      googleOptions?: {
        homeMobileCountryCode?: number;
        homeMobileNetworkCode?: number;
        radioType?: string;
        carrier?: string;
      };
    },
    verifyType?: number;
  }    

): Promise<{
  position: GeolocationLocation | null,
  addressInfo: Address | null | undefined
}> => {
  try {
    const geolocationPosition = await getGeolocation(param?.options);
    if (!geolocationPosition) {
      return { position: null, addressInfo: null };
    }
    
    const [err, result] = await to(getLocationDetail({ 
      latitude: geolocationPosition?.lat, 
      longitude: geolocationPosition?.lng,
      verifyType: param?.verifyType
    }));
    if (err) {
      return { position: null, addressInfo: null };
    } else {
      const addressInfo = result?.data || null;
      return { position: geolocationPosition, addressInfo };
    }
  } catch (error) {
    console.error('获取地址详情失败:', error);
    return { position: null, addressInfo: null };
  }
};

// 获取默认地址公共请求体-拦截器统一使用
export const getDefaultAddressBody = (): Record<string, any> => {
  const countryCode = getJDISiteKeys()?.["JDI-Country"];
  
  const defaultAddress = DEFAULT_COUNTRY_ADDRESSES[countryCode] || null;
  
  return defaultAddress ? {
    area: `${defaultAddress.provinceId}_${defaultAddress.cityId}_${defaultAddress.districtId}_${defaultAddress.townId}`,
    postCode: defaultAddress.postCode
  } : {};
};

/**
 * 处理区号数据：去掉加号并转成数字
 * @param areaCode 区号字符串，如 "+45"
 * @returns 处理后的数字，如果输入无效则返回null
 */
export const processAreaCode = (areaCode: string | undefined | null): number | null => {
  if (!areaCode) {
    return null;
  }
  
  const trimmedCode = areaCode.replace(/^\+/, '');
  
  if (!/^\d+$/.test(trimmedCode)) {
    return null;
  }
  
  const numericCode = Number(trimmedCode);
  
  if (isNaN(numericCode)) {
    return null;
  }
  return numericCode;
};

export const createRegex = (pattern: string) => {
  try {
    return new RegExp(pattern, 'u');
  } catch (error) {
    console.warn(`Failed to create regex with 'u' flag: ${error.message}`);
    return new RegExp(pattern);
  }
};